import { useRef } from 'react';
import { A11y, Mousewheel, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Flex, Heading, IconButton, useBreakpointValue, useToken } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { useCatalogItems } from '~/features/catalog/hooks/use-catalog-items';
import { getRelatedProgramKeysFromServer } from '~/features/program/controllers/get-related-program-keys-from-server';
import { useI18n } from '~/hooks/use-i18n';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

import { CatalogCard, CatalogCardSkeleton } from './catalog-card';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/mousewheel';

export type RelatedProgramsSectionProps = NonNullable<StaticRelatedProgramsSectionProps>;

export function RelatedProgramsSection({ programKeys, title }: RelatedProgramsSectionProps) {
  const { isRtl, i18n } = useI18n();
  /**
   * Swiper key is used to force a re-render of the swiper when the breakpoint changes.
   * This is necessary because the swiper is not re-initialized when the breakpoint changes.
   * This is a workaround to avoid the issue of the swiper not being re-initialized when the breakpoint changes.
   */
  const swiperKey = useBreakpointValue(
    {
      base: 'base',
      md: 'md',
      xl: 'xl',
    },
    { fallback: 'xl' }
  );
  const { catalogItems, catalogItemsIsLoading } = useCatalogItems(programKeys);
  const [contentMaxWidth] = useToken('sizes', ['content-max-width']);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const isMobile = swiperKey === 'base';

  return (
    <Box
      key={i18n.dir()}
      as='section'
      bgColor='supportGray.10'
      borderTop='1px solid'
      borderTopColor='supportBlue.30'
      isolation='isolate'
      overflow='hidden'
      padding={{
        base: '40px 0px',
        md: '56px 0px',
      }}
    >
      <ContentContainer>
        <Flex alignItems='center' gap={4} justifyContent='space-between' mb={8}>
          <Heading
            as='h3'
            size={{
              base: 'heading-6',
              md: 'heading-5',
            }}
          >
            {title}
          </Heading>
          <Flex
            gap={2}
            justifyContent='center'
            display={{
              base: 'none',
              xl: 'flex',
            }}
          >
            <IconButton
              ref={prevRef}
              aria-label='Previous'
              isDisabled={catalogItemsIsLoading}
              size='sm'
              variant='outline'
              icon={
                isRtl ? <ChevronRightIcon height='20px' width='20px' /> : <ChevronLeftIcon height='20px' width='20px' />
              }
            />
            <IconButton
              ref={nextRef}
              aria-label='Next'
              isDisabled={catalogItemsIsLoading}
              size='sm'
              variant='outline'
              icon={
                isRtl ? <ChevronLeftIcon height='20px' width='20px' /> : <ChevronRightIcon height='20px' width='20px' />
              }
            />
          </Flex>
        </Flex>
      </ContentContainer>
      <Box
        overflow='hidden'
        marginX={{
          base: '0px',
          md: `calc((100% - ${contentMaxWidth})/2)`,
        }}
        sx={{
          '.swiper .swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
        }}
      >
        <Swiper
          key={swiperKey}
          allowSlideNext={!catalogItemsIsLoading}
          allowSlidePrev={!catalogItemsIsLoading}
          centeredSlides={true}
          direction='horizontal'
          loop={true}
          modules={[A11y, Navigation, Mousewheel]}
          slidesPerView='auto'
          spaceBetween={16}
          breakpoints={{
            [480]: {
              slidesOffsetBefore: 24,
              slidesOffsetAfter: 24,
            },
            [1280]: {
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
              loop: false,
              centeredSlides: false,
            },
          }}
          mousewheel={{
            forceToAxis: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            if (swiper.params.navigation && typeof swiper.params.navigation === 'object') {
              swiper.changeLanguageDirection(i18n.dir());
            }
          }}
        >
          {catalogItemsIsLoading
            ? new Array(10).fill(null).map((_, idx) => (
                <SwiperSlide
                  key={idx}
                  style={{
                    width: isMobile ? '170px' : '237px',
                  }}
                >
                  <CatalogCardSkeleton width='100%' />
                </SwiperSlide>
              ))
            : catalogItems?.map((catalogItem, idx) => (
                <SwiperSlide
                  key={catalogItem?.key}
                  style={{
                    width: isMobile ? '170px' : '237px',
                  }}
                >
                  <CatalogCard
                    catalogItem={catalogItem}
                    priority={idx < 6}
                    width='100%'
                    onClick={() =>
                      trackEvent({
                        type: 'click',
                        label: 'related_programs_clicked',
                        name: 'Product Page CTA Clicked',
                        value: catalogItem.title!,
                        additionalAttributes: {
                          product_ids: [catalogItem.key].filter(Boolean),
                        },
                      })
                    }
                  />
                </SwiperSlide>
              ))}
        </Swiper>
      </Box>
    </Box>
  );
}

export function transformStaticRelatedProgramsSectionProps({ programKey }: { programKey: string }) {
  const programKeys = getRelatedProgramKeysFromServer(programKey);

  if (!programKeys?.length) {
    return null;
  }

  return {
    title: 'Other programs you might like:',
    programKeys,
  };
}

export type StaticRelatedProgramsSectionProps = ReturnType<typeof transformStaticRelatedProgramsSectionProps>;
